import { useEffect, useState } from 'react';

/**
 * Scrollspy: returns the id of the section currently in the viewport's upper
 * band (or '' before any is in view). Shared by the Header nav and the floating
 * NavBar so both highlight the same section as the user scrolls.
 *
 * `ids` may be a fresh array on each render — the effect keys off the joined
 * ids, so it only re-subscribes when the set of sections actually changes.
 */
export function useScrollSpy(ids: readonly string[]): string {
  const [active, setActive] = useState('');
  const key = ids.join(',');

  useEffect(() => {
    const sectionIds = key ? key.split(',') : [];
    if (sectionIds.length === 0) return;

    // Track which sections are currently inside the activation band. Deriving the
    // active id from this set (instead of only ever setting it) means the
    // highlight CLEARS when you scroll back to the hero/top, where no nav section
    // is in view — fixing the "stuck highlight" bug.
    const visible = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        });
        // The LAST section (lowest in document order) whose top has reached the
        // viewport's top band — i.e. the one currently filling the view. Picking
        // the last (not the first) intersecting id, with the band pinned to the
        // top of the viewport, means every section highlights as you scroll to
        // it — including ones near the page bottom that couldn't reach the old,
        // narrow mid-viewport band. Empty set → '' clears the highlight at the
        // hero/top.
        let current = '';
        sectionIds.forEach((id) => {
          if (visible.has(id)) current = id;
        });
        setActive(current);
      },
      // Activation band = the top 20% of the viewport (top: 0, bottom: -80%): a
      // section becomes active once its top scrolls up into that band.
      { rootMargin: '0px 0px -80% 0px', threshold: 0 }
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [key]);

  return active;
}
